/**
 * Pure upload-planning logic (spec-web-ui §2 "Upload"): given the project's
 * artifact rows and a file name, decide CREATE vs APPEND and build the exact
 * multipart "metadata" JSON for either path. Pure + DOM-free (compiles under
 * both tsconfigs, unit-tested in test/ui/upload-plan.test.ts); used by BOTH
 * the REST adapter (authoritative re-check at send time) and the browser
 * component (pre-send inline confirm — append-only must never surprise).
 *
 * Semantics MIRROR the MCP `upload_artifact` handler:
 *   - exact name match (`a.name === artifactName`, case-sensitive) —
 *     mcp/handlers.ts:610-612;
 *   - >1 match → ambiguous, refuse (mcp/handlers.ts:613-619);
 *   - 1 match → append a version; 0 → create (+v1).
 */

/**
 * Artifact type from the filename extension: LAST extension, lowercased,
 * "bin" fallback (no extension, leading dot, trailing dot). Byte-for-byte
 * mirror of the server-side `typeFromName` (mcp/handlers.ts:591-595) —
 * duplicated here because ui/ never imports server modules (bundle
 * isolation, ui/src/transport/types.ts header); if a third copy ever
 * appears, that is the CLAUDE.md shared-client-lib extraction trigger.
 */
export function typeFromName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "bin";
  return name.slice(dot + 1).toLowerCase();
}

/** The subset of ArtifactListRow the planner needs (structural — the
 * component and the adapter both hold full rows). */
export interface PlanRow {
  id: string;
  name: string;
  currentVersionNumber: number | null;
  versionCount: number;
}

/** Create metadata — src/domain/versions.ts:73-82 (createArtifactMetadataSchema). */
export interface CreateMetadata {
  name: string;
  type: string;
  author: string;
  message?: string;
}

/** Append metadata — src/domain/versions.ts createVersionMetadataSchema.
 * `baseVersionNumber` is the take-for-work guard (spec-artifact-locking
 * §4.2): the version the upload was edited from. Omitted entirely when the
 * caller declares none — an absent base behaves exactly as uploads did
 * before locking existed. */
export interface AppendMetadata {
  author: string;
  message?: string;
  baseVersionNumber?: number;
}

export type UploadPlan =
  | { kind: "create"; metadata: CreateMetadata }
  | {
      kind: "append";
      artifactId: string;
      artifactName: string;
      /** DISPLAY estimate for the confirm text ("vN"). The server assigns
       * the real number under a row lock (max+1 — src/domain/versions.ts
       * appendVersion); current pointer normally IS the max, so this is
       * right unless someone appends concurrently. */
      nextVersionNumber: number;
      /** The artifact's CURRENT version as the matched row reports it (0
       * when it has none). This is the honest base for an upload that was
       * never "taken for work" — the silent auto-lock path
       * (spec-artifact-locking §8) declares it so even an ad-hoc upload
       * gets the version guard. */
      currentVersionNumber: number;
      metadata: AppendMetadata;
    }
  | { kind: "ambiguous"; artifactName: string; artifactIds: string[] };

/** Normalize the optional message: trimmed; empty → the key is OMITTED
 * entirely (zod `.optional()` — an empty string would fail nothing but
 * store noise; absent is the honest value). */
function normalizeMessage(message: string | undefined): string | undefined {
  const trimmed = message?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * `baseVersionNumber` (optional, APPEND path only) is the take-for-work
 * guard the caller decided on (ui/src/lib/locking.ts baseVersionToDeclare);
 * a create ignores it — a brand-new artifact has nothing to be based on.
 */
export function planUpload(
  rows: readonly PlanRow[],
  fileName: string,
  author: string,
  message?: string,
  baseVersionNumber?: number,
): UploadPlan {
  const matches = rows.filter((r) => r.name === fileName);
  if (matches.length > 1) {
    return {
      kind: "ambiguous",
      artifactName: fileName,
      artifactIds: matches.map((r) => r.id),
    };
  }
  const msg = normalizeMessage(message);
  const existing = matches[0];
  if (existing) {
    const metadata: AppendMetadata = { author };
    if (msg !== undefined) metadata.message = msg;
    if (baseVersionNumber !== undefined) metadata.baseVersionNumber = baseVersionNumber;
    return {
      kind: "append",
      artifactId: existing.id,
      artifactName: existing.name,
      nextVersionNumber: (existing.currentVersionNumber ?? existing.versionCount) + 1,
      currentVersionNumber: existing.currentVersionNumber ?? 0,
      metadata,
    };
  }
  return {
    kind: "create",
    metadata:
      msg === undefined
        ? { name: fileName, type: typeFromName(fileName), author }
        : { name: fileName, type: typeFromName(fileName), author, message: msg },
  };
}
